import { translationKey } from '../i18n';
import { localize, readAppLanguage } from '../i18n';
import { scheduleWallTime } from '../utils/scheduleClock';
import { sheetSpacing } from '../design/tokens';
import SingleLineText from './ui/InternationalText';
import LoadingLabel from './LoadingLabel';
import React, { useState, useLayoutEffect, useMemo, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckIcon from '@mui/icons-material/Check';
import BottomSheet from './BottomSheet';

import { FONT_FAMILY as FONT_STACK } from '../design/tokens';

const pad2 = (n) => String(n).padStart(2, '0');
const nowHHMM = timeZone => scheduleWallTime(timeZone).slice(11, 16);
const clampToNow = (t, timeZone) => {
    const now = nowHHMM(timeZone);
    return isValidHHMM(t) && t > now ? now : t;
};
const isValidHHMM = (t) => {
    if (!/^\d{2}:\d{2}$/.test(t || '')) return false;
    const [h, m] = t.split(':').map(Number);
    return h >= 0 && h < 24 && m >= 0 && m < 60;
};
const addMinutes = (t, delta) => {
    if (!isValidHHMM(t)) return t;
    const [h, m] = t.split(':').map(Number);
    const day = 24 * 60;
    const total = (((h * 60 + m + delta) % day) + day) % day;
    return `${pad2(Math.floor(total / 60))}:${pad2(total % 60)}`;
};

const statusLabel = (status) => status === 'MISSED' ? translationKey('미복용') : translationKey('대기 중');
const statusBg = (status) => status === 'MISSED' ? '#fef2f2' : '#fffbeb';
const statusFg = (status) => status === 'MISSED' ? '#ef4444' : '#d97706';

export default function GuardianConfirmSheet({
    open, target, logs = [],
    onClose, onApply,
}) {
    const timeZone = logs.find(log => log.scheduleTimeZone)?.scheduleTimeZone;
    const scheduleNotice = (() => {
        if (!timeZone) return null;
        try {
            if (timeZone === new Intl.DateTimeFormat().resolvedOptions().timeZone) return null;
            const zone = new Intl.DateTimeFormat(readAppLanguage(), { timeZone, timeZoneName: 'longGeneric' })
                .formatToParts(new Date()).find(part => part.type === 'timeZoneName')?.value || timeZone;
            return localize('가족의 현지 시각 · {zone}', { zone });
        } catch (_) { return null; }
    })();
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [newTime, setNewTime] = useState(() => nowHHMM(timeZone));
    const [submitting, setSubmitting] = useState(false);
    const timeInputRef = useRef(null);
    const titleId = 'guardian-confirm-title';

    // 열릴 때마다 초기화: 모두 선택 + 현재 시각
    useLayoutEffect(() => {
        if (open) {
            setSelectedIds(new Set(logs.map(l => l.id)));
            setNewTime(nowHHMM(timeZone));
            setSubmitting(false);
        }
    }, [open, logs, timeZone]);



    const openTimePicker = () => {
        const el = timeInputRef.current;
        if (!el) return;
        try {
            if (typeof el.showPicker === 'function') { el.showPicker(); return; }
        } catch (_) {}
        try { el.focus(); el.click(); } catch (_) {}
    };

    const allSelected = logs.length > 0 && selectedIds.size === logs.length;
    const valid = isValidHHMM(newTime) && selectedIds.size > 0 && !submitting;
    const toggleOne = (id) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const toggleAll = () => {
        if (allSelected) setSelectedIds(new Set());
        else setSelectedIds(new Set(logs.map(l => l.id)));
    };

    const chips = [
        { label: translationKey('지금'), flex: 1, action: () => setNewTime(nowHHMM(timeZone)) },
        { label: '−30', flex: 1, action: () => setNewTime((t) => clampToNow(addMinutes(t, -30), timeZone)) },
        { label: '−10', flex: 1, action: () => setNewTime((t) => clampToNow(addMinutes(t, -10), timeZone)) },
        { label: '−5', flex: 1, action: () => setNewTime((t) => clampToNow(addMinutes(t, -5), timeZone)) },
    ];

    // The server accepts the patient's schedule-local time, not the caregiver's wall time.
    const buildConfirmedAt = timeHHMM =>
        `${scheduleWallTime(timeZone).slice(0, 10)}T${clampToNow(timeHHMM, timeZone)}:00`;

    const handleApply = async () => {
        if (!valid) return;
        setSubmitting(true);
        const ids = Array.from(selectedIds);
        const confirmedAt = buildConfirmedAt(newTime);
        try {
            await onApply(ids, confirmedAt);
            onClose();
        } catch (_) {
            // 실패 토스트는 부모가 띄우고, 시트는 열어둬서 같은 선택으로 재시도할 수 있게 한다.
        } finally {
            setSubmitting(false);
        }
    };

    const sortedLogs = useMemo(
        () => [...logs].sort((a, b) => (a.scheduledTime || '').localeCompare(b.scheduledTime || '')),
        [logs]
    );

    return (
        <BottomSheet
            dismissDisabled={submitting}
            open={open}
            onClose={onClose}
            borderRadius="22px 22px 0 0"
            handleColor="#E2DDD2"
            px="20px"
        >
            <Box role="region" aria-labelledby={titleId} sx={{ fontFamily: FONT_STACK }}>
                {/* 타이틀 */}
                <Box sx={{ mb: '16px' }}>
                    <SingleLineText
                        id={titleId}
                        sx={{
                            fontSize: '1.2rem',
                            fontWeight: 800,
                            letterSpacing: 0,
                            color: '#1F1D1B',
                            fontFamily: FONT_STACK,
                            lineHeight: 1.35,
                        }}
                    >{localize("처리할 약을 고르고 시간을 정해요")}</SingleLineText>
                    <Typography sx={{ mt: 1, fontSize: '0.9rem', color: '#666' }}>{localize("오늘 실제로 챙긴 시각을 입력해주세요. 자정을 넘긴 약은 다음 복용 시간 전까지 기록할 수 있어요.")}</Typography>
                    {scheduleNotice && <Typography sx={{ mt: 0.75, fontSize: '0.9rem', color: '#666' }}>{scheduleNotice}</Typography>}
                </Box>

                {/* 약 리스트 (체크박스) */}
                <Box sx={{
                    bgcolor: '#F1FBF3', border: '1px solid #D4ECDA', borderRadius: '12px',
                    py: '10px', px: '10px', mb: '12px',
                }}>
                    {/* 헤더 — 모두 선택 토글 */}
                    <Box
                        component="button"
                        type="button"
                        onClick={toggleAll}
                        sx={{
                            display: 'flex', alignItems: 'center', gap: 1,
                            width: '100%', border: 'none', bgcolor: 'transparent',
                            py: '7px', px: '6px', mb: '6px', cursor: 'pointer',
                            '&:active': { opacity: 0.7 },
                        }}
                    >
                        <Checkbox checked={allSelected} />
                        <Typography sx={{
                            fontSize: '1rem', fontWeight: 700, color: '#1F8A3D',
                            fontFamily: FONT_STACK,
                        }}>
                            {allSelected ? localize("모두 해제") : localize("모두 선택")}
                        </Typography>
                        <Box sx={{ flex: 1 }} />
                        <Typography sx={{
                            fontSize: '0.92rem', color: '#7A766F', fontFamily: FONT_STACK,
                        }}>
                            {selectedIds.size}/{logs.length}
                        </Typography>
                    </Box>

                    {/* 약 항목들 */}
                    {sortedLogs.map((log) => {
                        const checked = selectedIds.has(log.id);
                        return (
                            <Box
                                key={log.id}
                                role="button"
                                tabIndex={0}
                                onClick={() => toggleOne(log.id)}
                                sx={{
                                    display: 'flex', alignItems: 'center', gap: 1,
                                    p: 1.5, mb: 0.8,
                                    borderRadius: 3,
                                    bgcolor: '#fff',
                                    border: '1px solid #D4ECDA',
                                    cursor: 'pointer',
                                    '&:last-child': { mb: 0 },
                                    '&:active': { bgcolor: '#f9fafb' },
                                }}>
                                <Checkbox checked={checked} />
                                <Typography sx={{
                                    flex: 1, minWidth: 0,
                                    fontSize: '1.08rem', fontWeight: 700, color: '#1F1D1B',
                                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                    fontFamily: FONT_STACK,
                                }}>{log.medicationName}</Typography>
                                <Typography sx={{
                                    fontSize: '0.95rem', color: '#7A766F',
                                    fontVariantNumeric: 'tabular-nums', flexShrink: 0,
                                    fontFamily: FONT_STACK,
                                }}>{log.scheduledTime?.slice(0, 5)}</Typography>
                                <Box sx={{
                                    px: '8px', py: '2px', borderRadius: '8px',
                                    fontSize: '0.78rem', fontWeight: 700,
                                    bgcolor: statusBg(log.status),
                                    color: statusFg(log.status),
                                    flexShrink: 0,
                                    fontFamily: FONT_STACK,
                                }}>{localize(statusLabel(log.status))}</Box>
                            </Box>
                        );
                    })}
                </Box>

                {/* 시간 디스플레이 */}
                <SingleLineText sx={{
                    fontSize: '0.9rem', fontWeight: 700, color: '#7A766F',
                    fontFamily: FONT_STACK, mb: '6px', pl: '6px',
                }}>{localize("몇 시에 챙겼나요?")}</SingleLineText>
                <Box
                    onClick={openTimePicker}
                    sx={{
                        bgcolor: '#F1FBF3', border: '1px solid #D4ECDA', borderRadius: '14px',
                        py: '14px', px: '16px', mb: '12px',
                        display: 'flex', alignItems: 'center', gap: 1.5,
                        cursor: 'pointer',
                    }}
                >
                    <Box
                        sx={{
                            position: 'relative', flex: 1,
                            display: 'flex', alignItems: 'center', gap: 1.5,
                        }}>
                        <AccessTimeIcon sx={{ fontSize: 18, color: '#1F8A3D' }} />
                        <Typography
                            sx={{
                                fontSize: 28, fontWeight: 800, letterSpacing: 0,
                                color: '#1F8A3D', fontVariantNumeric: 'tabular-nums',
                                fontFamily: FONT_STACK, lineHeight: 1,
                            }}
                        >{newTime}</Typography>
                        <Box
                            component="input"
                            ref={timeInputRef}
                            type="time"
                            value={newTime}
                            onChange={(e) => { if (e.target.value) setNewTime(clampToNow(e.target.value, timeZone)); }}
                            onClick={(e) => {
                                e.stopPropagation();
                                try {
                                    if (typeof e.currentTarget.showPicker === 'function') {
                                        e.currentTarget.showPicker();
                                        return;
                                    }
                                } catch (_) {}
                                try { e.currentTarget.focus(); } catch (_) {}
                            }}
                            aria-label={localize("챙긴 시간 선택")}
                            sx={{
                                position: 'absolute', inset: 0,
                                opacity: 0, cursor: 'pointer',
                                border: 'none', background: 'transparent',
                                fontSize: 16,
                                color: 'transparent',
                                zIndex: 1,
                                '&::-webkit-calendar-picker-indicator': {
                                    width: '100%', height: '100%', cursor: 'pointer',
                                },
                            }}
                        />
                    </Box>
                    <Box
                        component="button"
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            setNewTime(nowHHMM(timeZone));
                        }}
                        aria-label={localize("지금 시각으로 설정")}
                        sx={{
                            border: '1px solid #D4ECDA', bgcolor: '#fff', color: '#1F8A3D',
                            fontSize: '0.9rem', fontWeight: 700, fontFamily: FONT_STACK,
                            py: '7px', px: '11px', borderRadius: 999, cursor: 'pointer',
                            flexShrink: 0,
                            position: 'relative',
                            zIndex: 2,
                            '&:active': { bgcolor: '#F1FBF3' },
                        }}
                    >{localize(translationKey("지금"))}</Box>
                </Box>

                {/* 빠른 선택 */}
                <SingleLineText sx={{
                    fontSize: '0.9rem', fontWeight: 700, color: '#7A766F',
                    fontFamily: FONT_STACK, mb: '6px', pl: '6px',
                }}>{localize("빠른 선택")}</SingleLineText>
                <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'nowrap', mb: sheetSpacing.action }}>
                    {chips.map((c, i) => (
                        <Box
                            key={i}
                            component="button"
                            type="button"
                            onClick={c.action}
                            sx={{
                                flex: c.flex || 1, minWidth: 0,
                                border: '1px solid #EDE8DD', bgcolor: '#fff', color: '#1F1D1B',
                                fontSize: '0.95rem', fontWeight: 600, fontFamily: FONT_STACK,
                                py: '8px', px: '6px', borderRadius: 999, cursor: 'pointer',
                                fontVariantNumeric: 'tabular-nums',
                                textAlign: 'center', whiteSpace: 'nowrap',
                                '&:active': { bgcolor: '#F8F5EE' },
                            }}
                        >{localize(c.label)}</Box>
                    ))}
                </Box>

                {/* 액션 */}
                <Box sx={{ display: 'flex', gap: '8px' }}>
                    <Box
                        component="button"
                        type="button"
                        onClick={onClose}
                        disabled={submitting}
                        sx={{
                            flex: 1,
                            border: '1px solid #EDE8DD', bgcolor: '#fff', color: '#7A766F',
                            fontSize: '1rem', fontWeight: 700, fontFamily: FONT_STACK,
                            py: '13px', px: 0, borderRadius: '12px', cursor: 'pointer',
                            '&:active': { bgcolor: '#F8F5EE' },
                        }}
                    >{localize("취소")}</Box>
                    <Box
                        component="button"
                        type="button"
                        disabled={!valid}
                        onClick={handleApply}
                        sx={{
                            flex: 2,
                            border: 'none',
                            bgcolor: valid ? '#ffe566' : '#f0f0f0',
                            color: valid ? '#5a4a00' : '#aaa',
                            fontSize: '1.1rem', fontWeight: 700, fontFamily: FONT_STACK,
                            py: '13px', px: 0, borderRadius: '12px',
                            cursor: valid ? 'pointer' : 'not-allowed',
                            boxShadow: valid ? '0 2px 8px rgba(230,168,0,0.22)' : 'none',
                            '&:active': valid ? { bgcolor: '#e8d040' } : {},
                        }}
                    >{submitting ? <LoadingLabel /> : localize("{v0}개 완료 처리", { v0: selectedIds.size })}</Box>
                </Box>
            </Box>
        </BottomSheet>
    );
}

function Checkbox({ checked }) {
    return (
        <Box sx={{
            width: 22, height: 22, borderRadius: '6px', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: checked ? '#1F8A3D' : '#fff',
            border: checked ? '1px solid #1F8A3D' : '1.5px solid #C7C0B3',
            transition: 'background-color 0.15s ease, border-color 0.15s ease',
        }}>
            {checked && <CheckIcon sx={{ fontSize: 14, color: '#fff' }} />}
        </Box>
    );
}
